// @/utils/session-utils.js

/**
 * Utilities for managing session state and detecting page reloads
 * 
 * Provides comprehensive session management with reliable reload detection,
 * redirect loop prevention, and robust storage persistence.
 * 
 * Features:
 * - Multi-method reload detection (Performance API, Navigation API, storage markers)
 * - Redirect loop prevention with timestamp-based tracking
 * - Auth-aware navigation handling
 * - Cross-tab synchronization
 * - Comprehensive session state reporting
 * - Graceful degradation for storage unavailability
 */

/**
 * Check if the current page load is a reload rather than first visit
 * Uses multiple detection methods for improved reliability
 * 
 * @returns {boolean} True if this appears to be a page reload
 */
export function isPageReload() {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    // Check multiple signals to determine if this is a page reload
    
    // 1. Check Performance API navigation type if available
    if (window.performance && window.performance.navigation) {
      if (window.performance.navigation.type === 1) {
        return true;
      }
    }
    
    // 2. Check Navigation Timing API if available 
    if (window.performance && window.performance.getEntriesByType) {
      const navEntries = window.performance.getEntriesByType('navigation');
      if (navEntries && navEntries.length > 0 && navEntries[0].type === 'reload') {
        return true;
      }
    }
    
    // 3. Fall back to session storage marker
    return sessionStorage.getItem('appSessionActive') === 'true';
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Error checking session state:', e);
    }
    return false;
  }
}

/**
 * Mark the current session as active for future reload detection
 * Sets persistent markers with timestamp for better tracking
 */
export function markSessionActive() {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    return;
  }

  try {
    sessionStorage.setItem('appSessionActive', 'true');
    sessionStorage.setItem('appSessionStartTime', Date.now().toString());
    
    // Additional marker in localStorage to help with cross-tab detection
    try {
      localStorage.setItem('lastActiveSession', Date.now().toString());
    } catch (e) {
      // localStorage might be disabled or quota exceeded - continue anyway
      if (process.env.NODE_ENV === 'development') {
        console.warn('Could not set localStorage marker:', e);
      }
    }
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Error setting session marker:', e);
    }
  }
}

/**
 * Get the time elapsed since initialization started
 * Useful for performance monitoring and timeout detection
 * 
 * @returns {number} Milliseconds since initialization or 0 if unknown
 */
export function getTimeSinceInit() {
  if (typeof sessionStorage === 'undefined') {
    return 0;
  }

  try {
    const startTimeStr = sessionStorage.getItem('appSessionStartTime');
    if (!startTimeStr) {
      return 0;
    }

    const startTime = parseInt(startTimeStr, 10);
    return Date.now() - startTime;
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Error calculating session time:', e);
    }
    return 0;
  }
}

/**
 * Get the navigation type (reload, navigate, back_forward)
 * Uses multiple detection methods for improved reliability
 * 
 * @returns {string} Navigation type or 'unknown'
 */
export function getNavigationType() {
  // Try using the Performance API first (Navigation Timing API)
  if (typeof window !== 'undefined' && window.performance) {
    const navEntries = window.performance.getEntriesByType?.('navigation');

    if (navEntries && navEntries.length > 0 && navEntries[0].type) {
      return navEntries[0].type; // 'navigate', 'reload', 'back_forward', 'prerender'
    }

    // Fallback to older Performance API
    if (window.performance.navigation) {
      const navType = window.performance.navigation.type;
      if (navType === 0) return 'navigate';
      if (navType === 1) return 'reload';
      if (navType === 2) return 'back_forward';
    }
  }

  // Fall back to our own session detection
  if (isPageReload()) {
    return 'reload';
  }

  // If path changed from stored path, this is navigation
  if (isNavigation()) {
    return 'navigate';
  }

  return 'unknown';
}

/**
 * Clear session markers
 * Completely removes all session tracking data
 */
export function clearSessionMarkers() {
  if (typeof sessionStorage === 'undefined') {
    return;
  }

  try {
    // Clear all session-related markers
    sessionStorage.removeItem('appSessionActive');
    sessionStorage.removeItem('appSessionStartTime');
    sessionStorage.removeItem('lastPath');
    sessionStorage.removeItem('loginAttempted');
    sessionStorage.removeItem('loginAttemptTimestamp');
    
    clearRedirectLoopCounter();
    
    // Also clean up localStorage marker if possible
    try {
      localStorage.removeItem('lastActiveSession');
    } catch (e) {
      // Ignore localStorage errors
      if (process.env.NODE_ENV === 'development') {
        console.warn('Error clearing localStorage marker:', e);
      }
    }
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Error clearing session markers:', e);
    }
  }
}

/**
 * Store the current URL for navigation tracking
 * Records the current path for future navigation detection
 */
export function storeCurrentPath() {
  if (typeof sessionStorage === 'undefined' || typeof window === 'undefined') {
    return;
  }

  try {
    sessionStorage.setItem('lastPath', window.location.pathname);
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Error storing current path:', e);
    }
  }
}

/**
 * Check if the current load is a navigation (path changed) rather than reload
 * Detects actual page changes vs simple reloads
 * 
 * @returns {boolean} True if this is a navigation to a new path
 */
export function isNavigation() {
  if (typeof sessionStorage === 'undefined' || typeof window === 'undefined') {
    return false;
  }

  try {
    const lastPath = sessionStorage.getItem('lastPath');
    return lastPath && lastPath !== window.location.pathname;
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Error checking navigation state:', e);
    }
    return false;
  }
}

/**
 * Check if we're in a redirect loop
 * Uses time-windowed detection for better accuracy
 * 
 * @returns {boolean} True if loop detected
 */
export function isInRedirectLoop() {
  if (typeof sessionStorage === 'undefined') return false;

  try {
    const loopCount = parseInt(sessionStorage.getItem('redirectLoopCount') || '0', 10);
    const timestamp = parseInt(sessionStorage.getItem('redirectLoopTimestamp') || '0', 10);
    const now = Date.now();

    // Reset counter if last redirect was more than 10 seconds ago
    if (now - timestamp > 10000) {
      sessionStorage.setItem('redirectLoopCount', '0');
      sessionStorage.setItem('redirectLoopTimestamp', now.toString());
      return false;
    }

    // Consider it a loop if we hit 3+ redirects in 10 seconds
    return loopCount >= 3;
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Error checking redirect loop:', e);
    }
    return false;
  }
}

/**
 * Track redirect count for loop detection
 * Provides auth-aware tracking with intentional exclusions
 * 
 * @returns {number} Current redirect count
 */
export function trackRedirect() {
  if (typeof sessionStorage === 'undefined') return 0;

  try {
    // Don't count redirects during auth code processing
    if (sessionStorage.getItem('processingAuthCode') === 'true') {
      if (process.env.NODE_ENV === 'development') {
        console.log('Skipping redirect tracking during auth code processing');
      }
      return 0;
    }

    // Check if this is part of the auth flow (from the URL)
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.has('code') || params.has('token') || params.has('invitation') || params.has('invitationToken')) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Skipping redirect tracking during auth parameter processing');
        }
        return 0;
      }

      // Also ignore redirects to intended logout page
      if (window.location.pathname === '/logout' && 
          sessionStorage.getItem('controlledLogout') === 'true') {
        if (process.env.NODE_ENV === 'development') {
          console.log('Skipping redirect tracking for intended logout');
        }
        return 0;
      }
    }

    const loopCount = parseInt(sessionStorage.getItem('redirectLoopCount') || '0', 10);
    const timestamp = parseInt(sessionStorage.getItem('redirectLoopTimestamp') || '0', 10);
    const now = Date.now();

    // Reset counter if last redirect was more than 10 seconds ago
    if (now - timestamp > 10000) {
      sessionStorage.setItem('redirectLoopCount', '1');
      sessionStorage.setItem('redirectLoopTimestamp', now.toString());
      return 1;
    }

    // Increment counter
    const newCount = loopCount + 1;
    sessionStorage.setItem('redirectLoopCount', newCount.toString());
    sessionStorage.setItem('redirectLoopTimestamp', now.toString());

    return newCount;
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Error tracking redirect:', e);
    }
    return 0;
  }
}

/**
 * Clear redirect loop counter
 * Provides complete cleanup of all redirect tracking data
 */
export function clearRedirectLoopCounter() {
  if (typeof sessionStorage === 'undefined') return;

  try {
    // Clear all redirect-tracking related items
    sessionStorage.removeItem('redirectLoopCount');
    sessionStorage.removeItem('redirectLoopTimestamp');
    sessionStorage.removeItem('loginAttempted');
    sessionStorage.removeItem('loginAttemptTimestamp');
    sessionStorage.removeItem('redirectAfterAuth');
    sessionStorage.removeItem('redirectedToKeycloak');
    sessionStorage.removeItem('redirectedToKeycloakTimestamp');

    if (process.env.NODE_ENV === 'development') {
      console.log('Cleared redirect loop counters');
    }
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Error clearing redirect counters:', e);
    }
  }
}

/**
 * Mark a login attempt to prevent attempt loops
 * Records login attempt with timestamp for future reference
 */
export function markLoginAttempted() {
  if (typeof sessionStorage === 'undefined') return;

  try {
    sessionStorage.setItem('loginAttempted', 'true');
    sessionStorage.setItem('loginAttemptTimestamp', Date.now().toString());
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Error marking login attempt:', e);
    }
  }
}

/**
 * Check if a login was recently attempted
 * Detects recent logins within a 10-second window
 * 
 * @returns {boolean} True if login was attempted in the last 10 seconds
 */
export function wasLoginRecentlyAttempted() {
  if (typeof sessionStorage === 'undefined') return false;

  try {
    const loginAttempted = sessionStorage.getItem('loginAttempted') === 'true';
    const timestamp = parseInt(sessionStorage.getItem('loginAttemptTimestamp') || '0', 10);
    const now = Date.now();

    // Consider recent if within 10 seconds
    return loginAttempted && (now - timestamp < 10000);
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Error checking recent login attempt:', e);
    }
    return false;
  }
}

/**
 * Get comprehensive session state information
 * Provides complete details about current session status
 * 
 * @returns {Object} Session state information
 */
export function getSessionState() {
  const state = {
    isActive: false,
    startTime: null,
    elapsedMs: 0,
    isReload: false,
    navType: 'unknown',
    hasRedirectLoop: false,
    redirectCount: 0,
    loginAttempted: false,
    processingAuth: false
  };
  
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    return state;
  }
  
  try {
    // Basic session state
    state.isActive = sessionStorage.getItem('appSessionActive') === 'true';
    
    const startTime = sessionStorage.getItem('appSessionStartTime');
    state.startTime = startTime ? parseInt(startTime, 10) : null;
    
    if (state.startTime) {
      state.elapsedMs = Date.now() - state.startTime;
    }
    
    // Navigation type
    state.isReload = isPageReload();
    state.navType = getNavigationType();
    
    // Redirect state
    state.redirectCount = parseInt(sessionStorage.getItem('redirectLoopCount') || '0', 10);
    state.hasRedirectLoop = state.redirectCount >= 3;
    
    // Auth state
    state.loginAttempted = sessionStorage.getItem('loginAttempted') === 'true';
    state.processingAuth = sessionStorage.getItem('processingAuthCode') === 'true';
    
    return state;
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Error getting session state:', e);
    }
    return state;
  }
}